import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { withCache } from '@ngneat/cashew';
import { Observable, of } from 'rxjs';
import { catchError, map, pluck, tap } from 'rxjs/operators';

import { ChartData } from '@containers/reports/dashboard/multi-chart/models/chartData.model';
import { StatKey, StatNamedCalculation } from '@containers/reports/dashboard/multi-chart/models/statKey.model';
import { FormatterService } from '@containers/reports/dashboard/services/formatter.service';
import { CacheService } from '@services/cache';
import { CustomHttpClient } from '@services/http-client';
import { CacheBucketIdEnum } from '@services/reports/models/cache-bucket-id.enum';
import { CACHE_KEYS } from '@services/reports/models/cache-keys.enum';
import { PreferencesService } from '@shared/services';
import { ISite } from '@shared/types';
import { IDatepickerValue } from '@xenial-ux/xux-datepicker/types';

interface JobGroups {
    _id: string;
    name: string;
    id: string;
    description: string;
    external_id: string;
    entity_id: string;
}

interface DashboardStatsData {
    title: {
        original: string;
        i18nTitle?: string | null;
    };
    activeLaborOption: boolean;
    execute_automatically: boolean;
    show_history_data: boolean;
    named_calculations: Array<StatNamedCalculation>;
    job_groups: Array<JobGroups>;
}

interface DashboardStatsResponse {
    data: DashboardStatsData;
}

@Injectable({
    providedIn: 'root',
})
export class DashboardServiceService {
    private namedCalculations: Array<StatNamedCalculation>;
    private job_groups: Array<JobGroups>;

    constructor(
        private formatter: FormatterService,
        private http: CustomHttpClient,
        private cacheService: CacheService,
        private preferences: PreferencesService,
    ) {}

    public getJobGroups(): Array<JobGroups> {
        return this.job_groups;
    }

    public getDefaultJobGroups(named_code: string): Array<JobGroups> {
        const namedCalculation = this.namedCalculations.find((nc) => nc.named_code === named_code);
        if (
            !namedCalculation ||
            !Array.isArray(namedCalculation.job_group_entity_ids) ||
            namedCalculation.job_group_entity_ids.length === 0
        ) {
            return this.job_groups;
        }
        return this.job_groups.filter((jg) => namedCalculation.job_group_entity_ids.includes(jg.entity_id));
    }

    public getComparisonDefault(named_code: string): boolean {
        const namedCalculation = this.namedCalculations.find((nc) => nc.named_code === named_code);
        if (!namedCalculation || namedCalculation.comparison_default === undefined) {
            return false;
        }
        return namedCalculation.comparison_default;
    }

    public getStats(companyId: string): Observable<DashboardStatsResponse> {
        const headers = new HttpHeaders({
            'x-company-id': companyId,
        });
        this.namedCalculations = [];
        this.job_groups = [];

        return this.http
            .get<DashboardStatsResponse>('/custom-reports/dashboard/settings', {
                headers,
            })
            .pipe(
                tap(({ data }) => {
                    this.namedCalculations = data.named_calculations;
                    this.job_groups = data.job_groups;
                }),
                catchError(() =>
                    of({
                        data: {
                            title: {
                                original: 'Dashboard',
                                i18nTitle: null,
                            },
                            activeLaborOption: false,
                            show_history_data: true,
                            execute_automatically: true,
                            named_calculations: [],
                            job_groups: [],
                        },
                    }),
                ),
            );
    }

    public getHeaderData({
        date,
        sites,
        stats,
        companyId,
        showHistoryData,
        ignoreCache,
        reportGroupingMethod,
    }: {
        date: IDatepickerValue;
        sites: Array<ISite>;
        stats: Array<Partial<StatKey>>;
        companyId: string;
        showHistoryData: boolean;
        ignoreCache: boolean;
        reportGroupingMethod: string;
    }): Observable<{ headerData: Array<Partial<StatKey>> }> {
        const headers = new HttpHeaders({
            'x-company-id': companyId,
        });
        const body = {
            sites: sites.map((site) => site.id),
            stats,
            showHistoryData,
            date_from: date.value.dateStart,
            date_to: date.value.dateEnd,
            reportGroupingMethod: reportGroupingMethod,
            job_groups: this.job_groups,
        };
        return this.http
            .post('/custom-reports/dashboard/header', body, {
                headers,
                context: withCache({
                    key: `${companyId}.${CACHE_KEYS.DASHBOARD.HEADER}`,
                    bucket: this.cacheService.cacheBuckets.find((bucket) => (bucket.name = CacheBucketIdEnum.DASHBOARD))
                        .bucket,
                    version: this.cacheService.getCacheSessionUUID(),
                    clearCachePredicate: () => ignoreCache,
                }),
            })
            .pipe(
                pluck('data'),
                map((data: any) => {
                    const headerData = data && data.headerData ? data.headerData : [];
                    headerData.forEach((stat) => {
                        try {
                            this.formatHeaderStat(stat);
                        } catch (error) {
                            console.error('[DashboardService] failed to format stat:', stat?.named_code, error);
                        }
                    });
                    return { headerData };
                }),
                catchError(() => of({ headerData: [] })),
            );
    }

    public getSourceData({
        date,
        sites,
        companyId,
        source,
        ignoreCache,
        showHistoryData,
        hasActiveLaborMatrix,
        reportGroupingMethod,
    }: {
        date: IDatepickerValue;
        sites: Array<ISite>;
        companyId: string;
        source: { uri: string; bucket_key: string };
        ignoreCache: boolean;
        showHistoryData: boolean;
        hasActiveLaborMatrix: boolean;
        reportGroupingMethod: string;
    }): Observable<{ data: { [key: string]: Array<ChartData> }; build: string; isItFromCache: boolean }> {
        const headers = new HttpHeaders({
            'x-company-id': companyId,
        });
        const body = {
            sites: sites.map((site) => site.id),
            date_from: date.value.dateStart,
            date_to: date.value.dateEnd,
            named_calculations: this.namedCalculations,
            showHistoryData,
            hasActiveLaborMatrix,
            reportGroupingMethod,
            jobGroups: this.job_groups,
        };
        return this.http
            .post(`/custom-reports/dashboard/${source.uri}`, body, {
                headers,
                context: withCache({
                    key: `${companyId}.${source.bucket_key}`,
                    bucket: this.cacheService.cacheBuckets.find((bucket) => (bucket.name = CacheBucketIdEnum.DASHBOARD))
                        .bucket,
                    version: this.cacheService.getCacheSessionUUID(),
                    clearCachePredicate: () => ignoreCache,
                }),
            })
            .pipe(catchError(() => of(null)));
    }

    private formatHeaderStat(stat: any): void {
        const relatedNamedCalculation = this.namedCalculations.find((nc) => nc.named_code === stat.named_code);
        stat.job_group_entity_ids = relatedNamedCalculation?.job_group_entity_ids || [];
        stat['shortValue'] = this.postFormatForNegatives(
            this.formatter.convertToShortInternational(stat.value || 0, stat.format),
        );
        stat['fullValue'] = this.postFormatForNegatives(
            this.formatter.convertToFullValueFormat(stat.value, stat.format),
        );
        if (stat.subValue) {
            stat.subValue.originalValue = stat.subValue.value;
            stat.subValue.value = this.formatter.convertToFullValueFormat(
                stat.subValue.value,
                stat.subValue.format,
                stat.subValue.precision,
            );
        }
        if (stat.additionalValue) {
            stat.additionalValue['shortValue'] = this.postFormatForNegatives(
                this.formatter.convertToShortInternational(
                    stat.additionalValue.value || 0,
                    stat.additionalValue.format,
                ),
            );
            stat.additionalValue['fullValue'] = this.postFormatForNegatives(
                this.formatter.convertToFullValueFormat(stat.additionalValue.value || 0, stat.additionalValue.format),
            );
        }
        if (stat.diffValue) {
            stat.diffValue['fullValue'] = this.diffValueFormatter(stat.diffValue.value);
        }
    }

    private postFormatForNegatives(value: string | number): string | number {
        if ((value as string).startsWith('(')) {
            return `<span class="red">${value}</span>`;
        }
        return value;
    }

    private diffValueFormatter(value) {
        const prepareResult = this.preferences.prepareCurrency({ value: Math.abs(value) });

        if (prepareResult) {
            const { currVal, currencySymbol } = prepareResult;
            const result = this.preferences.formatToCurrency(currVal, currencySymbol);

            return result;
        }
    }
}
